import React, { useEffect, useState } from "react";
import PageHeader from "../../components/PageHeader";
import PageActions from "../../components/PageActions";
import Table from "../../components/Table";
import Modal from "../../components/Modal";
import FilterModal from "../../components/FilterModal";
import Pagination from "../../components/Pagination";
import { useQuery } from "@tanstack/react-query";
import ReactSelect from "react-select";
import { ReceiptText, ScrollText } from "lucide-react";
import {
  FilasIntegracaoProvider,
  useFilasIntegracao,
  type FilaSalaoListItem,
} from "./context/FilasIntegracaoContext";
import { filasIntegracaoFilterFields } from "./constants/filterFields";
import { formatMesaSalaoLabel } from "../Painel/utils/origemPainel";
import { COLORS } from "../../constants";
import { BrazilDate } from "../../utils/BrazilDate";
import {
  FilaDoceDescricao,
  FilaDoceItem,
  FilaSalgadaItem,
  FilaSalgadaDescricao,
  filaPizzasService,
} from "../../services/filaPizzasService";

const statusLabel: Record<string, string> = {
  pending: "Pendente",
  done: "Finalizada",
  in_progress: "Em andamento",
  cancelled: "Cancelada",
};

const statusClass: Record<string, string> = {
  pending: "bg-warning text-white",
  done: "bg-success text-white",
  in_progress: "bg-info text-white",
  cancelled: "bg-error text-white",
};

const StatusPill: React.FC<{ status: string }> = ({ status }) => (
  <span
    className={`rounded-full px-2.5 py-1 text-xs font-semibold ${statusClass[status] || "bg-neutral text-white"}`}
  >
    {statusLabel[status] || status}
  </span>
);

const formatOrder = (displayId: number, orderId: number) =>
  `#${displayId} (${orderId})`;

const formatCreatedAt = (createdAt?: string | null) => {
  if (!createdAt) return "-";
  const value = String(createdAt);
  const hasExplicitTimezone = /Z$|[+-]\d{2}:\d{2}$/.test(value);

  const normalizedUtcValue = hasExplicitTimezone
    ? value
    : `${value.replace(" ", "T").replace(/Z$/i, "")}Z`;

  const date = new Date(normalizedUtcValue);
  if (Number.isNaN(date.getTime())) return "-";

  return BrazilDate.formatBRNaive(date, true);
};

const formatDescricao = (
  descricao: Array<FilaSalgadaDescricao | FilaDoceDescricao>
) => {
  if (!descricao || descricao.length === 0) {
    return "-";
  }

  const [first] = descricao;
  const sabores = first.sabores?.length ? first.sabores.join(", ") : "-";
  const extraCount = descricao.length > 1 ? ` +${descricao.length - 1}` : "";
  return `${first.name}${extraCount} | Sabores: ${sabores}`;
};

const TruncatedDescricao: React.FC<{ text: string }> = ({ text }) => (
  <div
    className="max-w-[360px] truncate text-sm"
    title={text}
    aria-label={text}
  >
    {text}
  </div>
);

type ItemFila = FilaSalgadaItem | FilaDoceItem | FilaSalaoListItem;
type StatusFilterOption = { value: "all" | "pending" | "done"; label: string };

const BobinaWrapper: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <div className="mx-auto max-h-[60vh] w-full max-w-md overflow-y-auto rounded-md border border-dashed border-neutral-300 bg-neutral-50 p-4 font-mono text-xs text-neutral-800">
    {children}
  </div>
);

const statusOptions: StatusFilterOption[] = [
  { value: "all", label: "Todos os status" },
  { value: "pending", label: "Pendentes" },
  { value: "done", label: "Finalizadas" },
];

const selectStyles = {
  control: (base: any) => ({
    ...base,
    minWidth: "220px",
    borderColor: COLORS.NEUTRAL_300,
    "&:hover": {
      borderColor: COLORS.PRIMARY,
    },
    "&:focus-within": {
      borderColor: COLORS.PRIMARY,
      boxShadow: "0 0 0 2px rgba(147, 32, 24, 0.2)",
    },
  }),
};

const FilasIntegracaoContent: React.FC = () => {
  const {
    filaSalgada,
    filaDoce,
    filaSalaoSalgada,
    filaSalaoDoce,
    currentPageSalgada,
    currentPageDoce,
    currentPageSalaoSalgada,
    currentPageSalaoDoce,
    totalPagesSalgada,
    totalPagesDoce,
    totalPagesSalaoSalgada,
    totalPagesSalaoDoce,
    totalItemsSalgada,
    totalItemsDoce,
    totalItemsSalaoSalgada,
    totalItemsSalaoDoce,
    itemsPerPage,
    isLoadingSalgada,
    isLoadingDoce,
    isLoadingSalaoSalgada,
    isLoadingSalaoDoce,
    isLoadingStats,
    salgadaStatus,
    doceStatus,
    salaoSalgadaStatus,
    salaoDoceStatus,
    filters,
    lojas,
    filterErrors,
    handleFieldChange,
    handleSearch,
    handleClear,
    clearFilterErrors,
    setSalgadaStatus,
    setDoceStatus,
    setSalaoSalgadaStatus,
    setSalaoDoceStatus,
    setCurrentPageSalgada,
    setCurrentPageDoce,
    setCurrentPageSalaoSalgada,
    setCurrentPageSalaoDoce,
    selectedDateISO,
    setSelectedDateISO,
    stats,
  } = useFilasIntegracao();

  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);

  const activeFiltersCount = Object.values(filters).filter(
    (value) => value !== null && value !== undefined && value !== ""
  ).length;

  const lojaIdForPedidoOriginal = (() => {
    const raw = String(filters.lojaId ?? "").trim();
    const n = Number(raw);
    if (raw !== "" && Number.isFinite(n)) return n;
    return undefined;
  })();

  const filterFields = filasIntegracaoFilterFields.map((field) => ({
    ...field,
    value: filters[field.name as keyof typeof filters] || "",
    options:
      field.name === "lojaId"
        ? [
            { value: "", label: "Todas as lojas" },
            ...lojas.map((loja) => ({
              value: String(loja.id),
              label: loja.nome,
            })),
          ]
        : field.options,
  }));

  const [selectedOriginalOrderId, setSelectedOriginalOrderId] = useState<
    number | null
  >(null);
  const [selectedSplitItem, setSelectedSplitItem] = useState<ItemFila | null>(
    null
  );
  const [originalItemsPage, setOriginalItemsPage] = useState(1);
  const originalItemsPerPage = 5;

  const isOriginalModalOpen = selectedOriginalOrderId !== null;
  const isSplitModalOpen = selectedSplitItem !== null;

  const { data: originalOrder, isLoading: isLoadingOriginalOrder } = useQuery({
    queryKey: [
      "filas-integracao",
      "pedido-original",
      selectedOriginalOrderId,
      lojaIdForPedidoOriginal,
    ],
    queryFn: () =>
      filaPizzasService.getPedidoOriginal(
        selectedOriginalOrderId!,
        lojaIdForPedidoOriginal
      ),
    enabled: !!selectedOriginalOrderId,
  });

  const closeOriginalModal = () => setSelectedOriginalOrderId(null);
  const closeSplitModal = () => setSelectedSplitItem(null);

  useEffect(() => {
    setOriginalItemsPage(1);
  }, [selectedOriginalOrderId]);

  const originalItems = originalOrder?.items || [];
  const originalTotalItems = originalItems.length;
  const originalTotalPages = Math.max(
    1,
    Math.ceil(originalTotalItems / originalItemsPerPage)
  );
  const originalStartIndex = (originalItemsPage - 1) * originalItemsPerPage;
  const paginatedOriginalItems = originalItems.slice(
    originalStartIndex,
    originalStartIndex + originalItemsPerPage
  );

  const renderActionButtons = (item: ItemFila) => (
    <div className="flex flex-wrap gap-2">
      <button
        type="button"
        className="rounded-md bg-primary p-2 text-white hover:bg-primary-dark"
        onClick={() => setSelectedOriginalOrderId(item.orderId)}
        title="Pedido original"
        aria-label="Pedido original"
      >
        <ReceiptText size={16} />
      </button>
      <button
        type="button"
        className="rounded-md bg-secondary p-2 text-white hover:brightness-95"
        onClick={() => setSelectedSplitItem(item)}
        title="Detalhes desmembrado"
        aria-label="Detalhes desmembrado"
      >
        <ScrollText size={16} />
      </button>
    </div>
  );

  const dataSalgada = filaSalgada.map((item) => [
    formatOrder(item.displayId, item.orderId),
    item.pedidoIntegracao?.customerName || "-",
    <TruncatedDescricao
      key={`salgada-desc-${item.id}`}
      text={formatDescricao(item.descricao)}
    />,
    <StatusPill key={`salgada-status-${item.id}`} status={item.status} />,
    formatCreatedAt(item.createdAt),
    renderActionButtons(item),
  ]);

  const dataDoce = filaDoce.map((item) => [
    formatOrder(item.displayId, item.orderId),
    item.pedidoIntegracao?.customerName || "-",
    <TruncatedDescricao
      key={`doce-desc-${item.id}`}
      text={formatDescricao(item.descricao)}
    />,
    <StatusPill key={`doce-status-${item.id}`} status={item.status} />,
    formatCreatedAt(item.createdAt),
    renderActionButtons(item),
  ]);

  const dataSalaoSalgada = filaSalaoSalgada.map((item) => [
    formatOrder(item.displayId, item.orderId),
    formatMesaSalaoLabel(item.pedidoFoodfly?.mesaId, item.pedidoFoodfly?.identificador) || "-",
    <TruncatedDescricao
      key={`salao-salgada-desc-${item.id}`}
      text={formatDescricao(item.descricao)}
    />,
    <StatusPill key={`salao-salgada-status-${item.id}`} status={item.status} />,
    formatCreatedAt(item.createdAt),
    renderActionButtons(item),
  ]);

  const dataSalaoDoce = filaSalaoDoce.map((item) => [
    formatOrder(item.displayId, item.orderId),
    formatMesaSalaoLabel(item.pedidoFoodfly?.mesaId, item.pedidoFoodfly?.identificador) || "-",
    <TruncatedDescricao
      key={`salao-doce-desc-${item.id}`}
      text={formatDescricao(item.descricao)}
    />,
    <StatusPill key={`salao-doce-status-${item.id}`} status={item.status} />,
    formatCreatedAt(item.createdAt),
    renderActionButtons(item),
  ]);

  return (
    <main className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
      <div className="mb-6 mt-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <PageHeader
          title="Filas Integração"
          description="Acompanhe as filas do Cardápio Web e do Salão (FoodFly)."
        />
        <PageActions
          onFilter={() => setIsFilterModalOpen(true)}
          onAdd={() => {}}
          addButtonText=""
          activeFiltersCount={activeFiltersCount}
          showAddButton={false}
        />
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <div className="rounded-lg bg-white p-4 shadow">
          <p className="text-sm text-neutral-500">Web salgada pendentes</p>
          <p className="text-2xl font-bold text-primary">
            {isLoadingStats ? "-" : stats?.filaSalgada.pendentes || 0}
          </p>
        </div>
        <div className="rounded-lg bg-white p-4 shadow">
          <p className="text-sm text-neutral-500">Web salgada finalizadas</p>
          <p className="text-2xl font-bold text-primary">
            {isLoadingStats ? "-" : stats?.filaSalgada.finalizadas || 0}
          </p>
        </div>
        <div className="rounded-lg bg-white p-4 shadow">
          <p className="text-sm text-neutral-500">Web doce pendentes</p>
          <p className="text-2xl font-bold text-primary">
            {isLoadingStats ? "-" : stats?.filaDoce.pendentes || 0}
          </p>
        </div>
        <div className="rounded-lg bg-white p-4 shadow">
          <p className="text-sm text-neutral-500">Web doce finalizadas</p>
          <p className="text-2xl font-bold text-primary">
            {isLoadingStats ? "-" : stats?.filaDoce.finalizadas || 0}
          </p>
        </div>
        <div className="rounded-lg bg-white p-4 shadow">
          <p className="text-sm text-neutral-500">Salão salgada pendentes</p>
          <p className="text-2xl font-bold text-neutral-800">
            {isLoadingStats ? "-" : stats?.filaSalaoSalgada?.pendentes || 0}
          </p>
        </div>
        <div className="rounded-lg bg-white p-4 shadow">
          <p className="text-sm text-neutral-500">Salão salgada finalizadas</p>
          <p className="text-2xl font-bold text-neutral-800">
            {isLoadingStats ? "-" : stats?.filaSalaoSalgada?.finalizadas || 0}
          </p>
        </div>
        <div className="rounded-lg bg-white p-4 shadow">
          <p className="text-sm text-neutral-500">Salão doce pendentes</p>
          <p className="text-2xl font-bold text-neutral-800">
            {isLoadingStats ? "-" : stats?.filaSalaoDoce?.pendentes || 0}
          </p>
        </div>
        <div className="rounded-lg bg-white p-4 shadow">
          <p className="text-sm text-neutral-500">Salão doce finalizadas</p>
          <p className="text-2xl font-bold text-neutral-800">
            {isLoadingStats ? "-" : stats?.filaSalaoDoce?.finalizadas || 0}
          </p>
        </div>
      </div>

      <div className="mt-8 rounded-lg bg-white p-4 shadow">
        <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <h3 className="text-lg font-semibold text-neutral-800">
              Cardápio Web — Salgada
            </h3>
            <input
              type="date"
              value={selectedDateISO}
              onChange={(e) => setSelectedDateISO(e.target.value)}
              className="rounded-md border border-neutral-300 px-3 py-2 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
          <ReactSelect
            options={statusOptions}
            isClearable={false}
            value={statusOptions.find(
              (option) => option.value === salgadaStatus
            )}
            onChange={(option) => setSalgadaStatus(option?.value || "all")}
            styles={selectStyles}
          />
        </div>

        <Table
          headers={[
            "Pedido",
            "Cliente",
            "Descrição",
            "Status",
            "Criado em",
            "Detalhes",
          ]}
          data={dataSalgada}
        />
        {isLoadingSalgada && (
          <div className="mt-3 text-sm text-neutral-500">
            Carregando fila salgada...
          </div>
        )}
        {totalPagesSalgada > 0 && (
          <div className="mt-4">
            <Pagination
              currentPage={currentPageSalgada}
              totalPages={totalPagesSalgada}
              totalItems={totalItemsSalgada}
              itemsPerPage={itemsPerPage}
              onPageChange={setCurrentPageSalgada}
            />
          </div>
        )}
      </div>

      <div className="mt-8 rounded-lg bg-white p-4 shadow">
        <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <h3 className="text-lg font-semibold text-neutral-800">
              Cardápio Web — Doce
            </h3>
            <input
              type="date"
              value={selectedDateISO}
              onChange={(e) => setSelectedDateISO(e.target.value)}
              className="rounded-md border border-neutral-300 px-3 py-2 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
          <ReactSelect
            options={statusOptions}
            isClearable={false}
            value={statusOptions.find((option) => option.value === doceStatus)}
            onChange={(option) => setDoceStatus(option?.value || "all")}
            styles={selectStyles}
          />
        </div>

        <Table
          headers={[
            "Pedido",
            "Cliente",
            "Descrição",
            "Status",
            "Criado em",
            "Detalhes",
          ]}
          data={dataDoce}
        />
        {isLoadingDoce && (
          <div className="mt-3 text-sm text-neutral-500">
            Carregando fila doce...
          </div>
        )}
        {totalPagesDoce > 0 && (
          <div className="mt-4">
            <Pagination
              currentPage={currentPageDoce}
              totalPages={totalPagesDoce}
              totalItems={totalItemsDoce}
              itemsPerPage={itemsPerPage}
              onPageChange={setCurrentPageDoce}
            />
          </div>
        )}
      </div>


      <div className="mt-8 rounded-lg bg-white p-4 shadow">
        <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <h3 className="text-lg font-semibold text-neutral-800">
              Salão Salgada (FoodFly)
            </h3>
            <input
              type="date"
              value={selectedDateISO}
              onChange={(e) => setSelectedDateISO(e.target.value)}
              className="rounded-md border border-neutral-300 px-3 py-2 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
          <ReactSelect
            options={statusOptions}
            isClearable={false}
            value={statusOptions.find(
              (option) => option.value === salaoSalgadaStatus
            )}
            onChange={(option) =>
              setSalaoSalgadaStatus(option?.value || "all")
            }
            styles={selectStyles}
          />
        </div>

        <Table
          headers={[
            "Pedido",
            "Mesa",
            "Descrição",
            "Status",
            "Criado em",
            "Detalhes",
          ]}
          data={dataSalaoSalgada}
        />
        {isLoadingSalaoSalgada && (
          <div className="mt-3 text-sm text-neutral-500">
            Carregando fila salão salgada...
          </div>
        )}
        {totalPagesSalaoSalgada > 0 && (
          <div className="mt-4">
            <Pagination
              currentPage={currentPageSalaoSalgada}
              totalPages={totalPagesSalaoSalgada}
              totalItems={totalItemsSalaoSalgada}
              itemsPerPage={itemsPerPage}
              onPageChange={setCurrentPageSalaoSalgada}
            />
          </div>
        )}
      </div>

      <div className="mt-8 rounded-lg bg-white p-4 shadow">
        <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <h3 className="text-lg font-semibold text-neutral-800">
              Salão Doce (FoodFly)
            </h3>
            <input
              type="date"
              value={selectedDateISO}
              onChange={(e) => setSelectedDateISO(e.target.value)}
              className="rounded-md border border-neutral-300 px-3 py-2 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
          <ReactSelect
            options={statusOptions}
            isClearable={false}
            value={statusOptions.find(
              (option) => option.value === salaoDoceStatus
            )}
            onChange={(option) => setSalaoDoceStatus(option?.value || "all")}
            styles={selectStyles}
          />
        </div>

        <Table
          headers={[
            "Pedido",
            "Mesa",
            "Descrição",
            "Status",
            "Criado em",
            "Detalhes",
          ]}
          data={dataSalaoDoce}
        />
        {isLoadingSalaoDoce && (
          <div className="mt-3 text-sm text-neutral-500">
            Carregando fila salão doce...
          </div>
        )}
        {totalPagesSalaoDoce > 0 && (
          <div className="mt-4">
            <Pagination
              currentPage={currentPageSalaoDoce}
              totalPages={totalPagesSalaoDoce}
              totalItems={totalItemsSalaoDoce}
              itemsPerPage={itemsPerPage}
              onPageChange={setCurrentPageSalaoDoce}
            />
          </div>
        )}
      </div>

      <FilterModal
        isOpen={isFilterModalOpen}
        onClose={() => {
          clearFilterErrors();
          setIsFilterModalOpen(false);
        }}
        fields={filterFields}
        errors={filterErrors}
        onFieldChange={handleFieldChange}
        onSearch={() => {
          if (handleSearch()) setIsFilterModalOpen(false);
        }}
        onClear={handleClear}
        gridClassName="grid grid-cols-2 gap-4"
      />

      <Modal
        isOpen={isOriginalModalOpen}
        onClose={closeOriginalModal}
        title="Pedido Original"
      >
        <BobinaWrapper>
          {isLoadingOriginalOrder ? (
            <p>Carregando pedido original...</p>
          ) : !originalOrder ? (
            <p>Pedido original não encontrado.</p>
          ) : (
            <>
              <p className="text-center font-bold">DOM BELLONI</p>
              <p>Pedido: #{originalOrder.displayId || "-"}</p>
              <p>Nº do pedido (Original): {originalOrder.orderId}</p>
              <p>Cliente: {originalOrder.customerName || "-"}</p>
              <p>Tipo: {originalOrder.orderType || "-"}</p>
              <p>Obs. pedido: {originalOrder.observation || "-"}</p>
              <p className="my-2 border-t border-dashed border-neutral-400" />
              {paginatedOriginalItems.map((item, itemIndex) => (
                <div
                  key={`${item.order_item_id}-${itemIndex}`}
                  className="mb-3"
                >
                  <p>
                    {item.quantity}x {item.name}
                  </p>
                  {item.options?.map((option, optionIndex) => (
                    <p
                      key={`${item.order_item_id}-${option.option_id}-${optionIndex}`}
                    >
                      - {option.name}
                    </p>
                  ))}
                  <p>Obs. item: {item.observation || "-"}</p>
                </div>
              ))}
            </>
          )}
        </BobinaWrapper>
        {!isLoadingOriginalOrder && originalTotalItems > 0 && (
          <div className="mt-4">
            <Pagination
              currentPage={originalItemsPage}
              totalPages={originalTotalPages}
              totalItems={originalTotalItems}
              itemsPerPage={originalItemsPerPage}
              onPageChange={setOriginalItemsPage}
            />
          </div>
        )}
      </Modal>

      <Modal
        isOpen={isSplitModalOpen}
        onClose={closeSplitModal}
        title="Detalhes Desmembrado"
      >
        <BobinaWrapper>
          {!selectedSplitItem ? (
            <p>Nenhum item selecionado.</p>
          ) : (
            <>
              <p className="text-center font-bold">DETALHES DA FILA</p>
              <p>Pedido: #{selectedSplitItem.displayId}</p>
              <p>Order ID: {selectedSplitItem.orderId}</p>
              <p>
                Status:{" "}
                {statusLabel[selectedSplitItem.status] ||
                  selectedSplitItem.status}
              </p>
              <p className="my-2 border-t border-dashed border-neutral-400" />
              {selectedSplitItem.descricao.map((detail, index) => (
                <div
                  key={`detail-${selectedSplitItem.id}-${index}`}
                  className="mb-3"
                >
                  <p className="font-semibold">Item {index + 1}</p>
                  <p>Nome: {detail.name || "-"}</p>
                  <p>Quantidade: {detail.quantidade || 1}</p>
                  <p>Borda: {detail.borda || "Sem borda"}</p>
                  <p>
                    Sabores:{" "}
                    {detail.sabores && detail.sabores.length
                      ? detail.sabores.join(" | ")
                      : "Sem sabores"}
                  </p>
                  <p>Item original: {detail.item_original_name || "-"}</p>
                  <p>Observação: {detail.observacao || "-"}</p>
                  <p className="my-2 border-t border-dashed border-neutral-300" />
                </div>
              ))}
            </>
          )}
        </BobinaWrapper>
      </Modal>
    </main>
  );
};

const FilasIntegracao: React.FC = () => {
  return (
    <FilasIntegracaoProvider>
      <FilasIntegracaoContent />
    </FilasIntegracaoProvider>
  );
};

export default FilasIntegracao;
